<mat-expansion-panel class="nms-about-info-panel mx-14 mb-8">
  <mat-expansion-panel-header class="text-lg font-medium text-slate-900">
    <mat-panel-title class="flex items-center gap-2 text-lg font-medium">
      <mat-icon class="text-slate-500" aria-hidden="true">info</mat-icon>
      <span class="text-slate-500">How device joining works</span>
    </mat-panel-title>
  </mat-expansion-panel-header>
  <ul class="m-0 list-disc space-y-2 pb-6 pr-6 pl-10 text-base leading-[1.45] text-slate-500">
    <li>A configured gateway is required to add devices automatically. You can check your gateway status below.</li>
    <li>Compatible devices can then join your building automatically when you enable device joining. Devices that don’t support this method must be added using the app.</li>
    <li>Have a configured gateway but still can’t use this feature? Contact our Support team to check whether additional configuration is required.</li>
  </ul>
</mat-expansion-panel>
<main class="nms-page" data-cy="nms-page">
  @if (contextError()) {
    <section class="status-panel text-slate-700 bg-white status-panel-error nms-page-status" data-cy="nms-page-error">
      {{ contextError() }}
    </section>
  }

  <header class="nms-page-header font-medium h-16 text-2xl flex items-center">
    <span class="font-medium">Device Joining Portal</span>
  </header>
  <div class="mx-14">
    <mat-card class="device-joining-card my-8">
      <mat-card-header class="block">
        <mat-card-title class="title">
          <div class="flex flex-col">
            <span class="font-medium text-lg">Joining Session</span>
            <div class="status-container pt-4">
              @if (startJoining()) {
                <span class="dot inactive"></span>
                <span class="status-text text-base text-slate-500">Inactive</span>
              } @else {
                <span class="dot active"></span>
                <span class="status-text text-base text-slate-500">Active</span>
              }
            </div>
          </div>
          <div class="flex justify-end">
            @if (startJoining()) {
              <button
                type="button"
                class="px-3 py-1 bg-[#22C55E] text-white rounded-lg transition-colors flex items-center gap-2 text-lg"
                data-cy="nms-enable-device-joining"
                (click)="startDeviceJoining()"
                [disabled]="!isReadyForJoining() || joiningSessionAction() !== 'idle'"
              >
                <span class="icon icon-play" aria-hidden="true"></span>
                {{ joiningSessionAction() === 'starting' ? 'Starting Session' : 'Enable Device Joining' }}
              </button>
            } @else {
              <button
                type="button"
                class="px-2 py-1 bg-[#ef4444] text-white rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 text-base"
                data-cy="nms-stop-session"
                (click)="stopDeviceJoining()"
                [disabled]="joiningSessionAction() !== 'idle'">
                <span class="icon icon-square" aria-hidden="true"></span>
                {{ joiningSessionAction() === 'stopping' ? 'Stopping Session' : 'Stop Session' }}
              </button>
            }
            <button
              type="button"
              class="flex items-center justify-center text-slate-500 transition-colors hover:text-slate-700 px-1"
              data-cy="joining-session-toggle"
              (click)="toggleJoiningSessionDetails()"
              [attr.aria-expanded]="joiningSessionDetailsVisible()"
              aria-label="Toggle joining session details">
              <span
                class="inline-block text-[10px] leading-none transition-transform"
                [style.transform]="joiningSessionDetailsVisible() ? 'rotate(180deg)' : 'rotate(0deg)'"
                aria-hidden="true"
              >▼</span>
            </button>
          </div>
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        @if (sessionActionError()) {
          <p class="text-sm text-rose-600" role="alert">{{ sessionActionError() }}</p>
        }
        @if (joiningSessionDetailsVisible()) {
          <div class="w-full rounded-2xl nms-enabled bg-white px-4 py-4 mt-2">
            @if (openJoiningStatus(); as joiningStatus) {
              <div class="grid gap-3 sm:grid-cols-3 xl:grid-cols-3">
                <div class="flex items-center gap-2 text-sm text-slate-700">
                  @if (joiningStatus.nmsNetworkExists) {
                    <span class="text-emerald-500">✓</span>
                  } @else {
                    <span class="text-rose-500">✕</span>
                  }
                  NMS network exists
                </div>
                <div class="flex items-center gap-2 text-sm text-slate-700">
                  @if (joiningStatus.networkSecurityKeysActive) {
                    <span class="text-emerald-500">✓</span>
                  } @else {
                    <span class="text-rose-500">✕</span>
                  }
                  <span>Network security keys active</span>
                </div>
                <div class="flex items-center gap-2 text-sm text-slate-700">
                  @if (joiningStatus.keyManagementSecurityKeysActive) {
                    <span class="text-emerald-500">✓</span>
                  } @else {
                    <span class="text-rose-500">✕</span>
                  }
                  <span>Key management security keys active</span>
                </div>
              </div>
            } @else {
              <div class="h-12 w-full rounded-xl border border-dashed border-slate-200 bg-slate-50"></div>
            }
          </div>
        }
        <div class="card-row-grid pt-6">
          @if (latestSessionStatus(); as sessionStatus) {
            <mat-card class="container">
              <mat-card-header>
                <mat-card-title class="text-slate-500">
                  <span class="inline-flex items-center gap-2 whitespace-nowrap text-base">
                    <span class="icon icon-radio" aria-hidden="true"></span>
                    <span>Active Gateways</span>
                  </span>
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="mt-2">
                <span class="text-2xl font-medium">{{sessionStatus.activeGateways}}</span>
              </mat-card-content>
            </mat-card>
            <mat-card class="container">
              <mat-card-header>
                <mat-card-title class="text-slate-500 text-xs">
                  <span class="text-base">Nodes Found</span>
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="mt-2">
                <span class="text-2xl font-medium">{{sessionStatus.nodesFound}}</span>
              </mat-card-content>
            </mat-card>
            <mat-card class="container">
              <mat-card-header>
                <mat-card-title class="text-slate-500">
                  <span class="text-base">Nodes Joined</span>
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="mt-2">
                <span class="text-2xl font-medium">{{sessionStatus.nodesJoined}}</span>
              </mat-card-content>
            </mat-card>
            <mat-card class="container">
              <mat-card-header>
                <mat-card-title class="text-slate-500">
                  <span class="inline-flex items-center gap-2 whitespace-nowrap text-base">
                    <span class="icon icon-clock" aria-hidden="true"></span>
                    <span>Duration</span>
                  </span>
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="mt-2">
                <span class="text-2xl font-medium">{{formatDuration(sessionStatus.sessionDurationSeconds)}}</span>
              </mat-card-content>
            </mat-card>
            <mat-card class="container">
              <mat-card-header>
                <mat-card-title class="text-slate-500">
                  <span class="text-base">Join Rate</span>
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="mt-2">
                <span class="text-2xl font-medium">{{(sessionStatus.nodesJoined * 60 / sessionStatus.sessionDurationSeconds) | number:'1.0-0'}}</span>/min
              </mat-card-content>
            </mat-card>
          }
        </div>
      </mat-card-content>
    </mat-card>

    <mat-card class="joining-progress-card mb-8">
      <mat-card-header>
        <mat-card-title>
          <span class="font-medium text-lg">Joining Progress</span>
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        @if (latestSessionStatus(); as sessionStatus) {
          <div class="card-row-grid pt-6">
            <mat-card class="success-card">
              <mat-card-header>
                <mat-card-title>
                  <span class="inline-flex items-center gap-2 whitespace-nowrap title-with-icon-end text-base">
                    <span>Successful Joins</span>
                    <span class="icon icon-trending-up" aria-hidden="true"></span>
                  </span>
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="mt-2">
                <span class="text-2xl font-medium">{{sessionStatus.nodesJoined}}</span>
              </mat-card-content>
            </mat-card>
            <mat-card class="fail-card">
              <mat-card-header>
                <mat-card-title>
                  <span class="inline-flex items-center gap-2 whitespace-nowrap title-with-icon-end text-base">
                    <span>Failed Attempts</span>
                    <span class="icon icon-trending-down" aria-hidden="true"></span>
                  </span>
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="mt-2">
                <span class="text-2xl font-medium">{{sessionStatus.joinFailedNodes}}</span>
              </mat-card-content>
            </mat-card>
            <mat-card class="info-card">
              <mat-card-header>
                <mat-card-title>
                  <span class="inline-flex items-center gap-2 whitespace-nowrap title-with-icon-end text-base">
                    <span>In Progress</span>
                    <span class="icon icon-activity" aria-hidden="true"></span>
                  </span>
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="mt-2">
                <span class="text-2xl font-medium" data-cy="nms-in-progress-count">
                  {{sessionStatus.inProgressNodes}}
                </span>
              </mat-card-content>
            </mat-card>
          </div>
        }
      </mat-card-content>
    </mat-card>

    <div class="mb-8">
      <mat-expansion-panel class="nms-expansion-panel">
        <mat-expansion-panel-header class="text-lg font-medium text-slate-900" data-cy="gateway-status-panel-trigger">
          <mat-panel-title class="text-lg font-medium text-slate-900">Gateway Status</mat-panel-title>
        </mat-expansion-panel-header>
        <div class="gateway-table-wrap grid w-full gap-4">
          @if (loading() && !gatewayStatusRows().length) {
            <div class="gateway-table-empty text-slate-700 bg-white" data-cy="gateway-status-loading">
              {{ 'Loading...' }}
            </div>
          } @else if (!gatewayStatusRows().length) {
            <div class="gateway-table-empty text-slate-700 bg-white" data-cy="gateway-status-empty">
              {{ 'No Data' }}
            </div>
          } @else {
            <table mat-table [dataSource]="gatewayStatusDataSource" class="gateway-table" data-cy="gateway-status-table">
              <ng-container matColumnDef="gateway">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Gateway</th>
                <td mat-cell *matCellDef="let row">
                  <div class="flex flex-col gap-1">
                    <span class="font-normal text-slate-900">{{ row.gateway }}</span>
                    @if (row.gatewaySerialNumber && row.gatewaySerialNumber !== row.gateway) {
                      <span class="font-medium text-slate-500">{{ row.gatewaySerialNumber }}</span>
                    }
                  </div>
                </td>
              </ng-container>

              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Status</th>
                <td mat-cell *matCellDef="let row">
                  <span class="inline-flex items-center gap-2 font-normal text-slate-900">
                    <span
                      class="status-dot"
                      [class.bg-green-500]="row.status === 'HEALTHY'"
                      [class.status-dot--degraded]="row.status === 'DEGRADED'"
                      [class.bg-red-500]="row.status === 'OFFLINE'">

                    </span>
                    <span>
                      @if (row.status === 'HEALTHY') {
                        Healthy
                      } @else if (row.status === 'DEGRADED') {
                        Degraded
                      } @else {
                        Offline
                      }
                    </span>
                  </span>
                </td>
              </ng-container>

              <ng-container matColumnDef="deviceJoining">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Device Joining</th>
                <td mat-cell *matCellDef="let row">
                  <span
                    class="inline-flex min-w-18 items-center justify-center rounded-full px-3 py-1 font-normal"
                    [class.bg-emerald-100]="row.deviceJoining === 'ACTIVE'"
                    [class.text-emerald-700]="row.deviceJoining === 'ACTIVE'"
                    [class.bg-slate-200]="row.deviceJoining === 'INACTIVE'"
                    [class.text-slate-600]="row.deviceJoining === 'INACTIVE'">
                    {{ row.deviceJoining === 'ACTIVE' ? 'Active' : 'Inactive' }}
                  </span>
                </td>
              </ng-container>

              <ng-container matColumnDef="nodesJoined">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Nodes Joined</th>
                <td mat-cell *matCellDef="let row"><span class="font-normal">{{ row.nodesJoined }}</span></td>
              </ng-container>

              <ng-container matColumnDef="lastJoin">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Last Join</th>
                <td mat-cell *matCellDef="let row">
                  <span class="font-medium text-slate-500" [class.text-slate-400]="!row.lastJoinAt">
                    {{ row.lastJoinAt ? (row.lastJoinAt | date:'medium') : 'N/A' }}
                  </span>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="gatewayColumns"></tr>
              <tr
                mat-row
                *matRowDef="let row; columns: gatewayColumns"
                [class.is-highlighted]="row.highlighted"
                [attr.data-cy]="'gateway-status-row-' + row.gatewayCode"></tr>
            </table>
          }

          <app-pagination
            [section]="'gateway-status'"
            [position]="'bottom'"
            [rows]="gatewayStatusRows()"
            [dataSource]="gatewayStatusDataSource" />
        </div>
      </mat-expansion-panel>
    </div>

    <div class="mb-8">
      <mat-expansion-panel class="nms-expansion-panel">
        <mat-expansion-panel-header class="text-lg font-medium text-slate-900" data-cy="node-joining-status-panel-trigger">
          <mat-panel-title class="text-lg font-medium text-slate-900">Device Joining Status</mat-panel-title>
        </mat-expansion-panel-header>
        <div class="grid w-full gap-4">
          @if (nodeJoiningLoading() && !nodeJoiningDataSource.data.length) {
            <div class="gateway-table-empty text-slate-700 bg-white" data-cy="node-joining-loading">
              {{ 'Loading...' }}
            </div>
          } @else if (nodeJoiningError()) {
            <div class="gateway-table-empty text-slate-700 bg-white" data-cy="node-joining-error">
              {{ nodeJoiningError() }}
            </div>
          } @else {
            <table
              mat-table
              [dataSource]="nodeJoiningDataSource"
              class="node-joining-table"
              data-cy="node-joining-table"
            >
              <ng-container matColumnDef="nodeAddress">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Node Address</th>
                <td mat-cell *matCellDef="let row">{{ row.nodeAddress }}</td>
              </ng-container>

              <ng-container matColumnDef="uid">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">UID</th>
                <td mat-cell *matCellDef="let row" class="font-medium text-slate-500">{{ row.uid || '-' }}</td>
              </ng-container>

              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Status</th>
                <td mat-cell *matCellDef="let row">
                  <div class="node-status" [class.node-status--failed]="row.status === 'FAILED'">
                    <span
                      class="node-status-icon"
                      [class.node-status-icon--joined]="row.status === 'JOINED'"
                      [class.node-status-icon--failed]="row.status === 'FAILED'"
                      [class.node-status-icon--attempting]="row.status === 'ATTEMPTING'"
                      [class.node-status-icon--unknown]="row.status === 'UNKNOWN'"
                      aria-hidden="true"></span>
                    <div class="node-status-copy">
                      <span class="node-status-label">
                        {{
                          row.status === 'JOINED'
                            ? 'Joined'
                            : row.status === 'FAILED'
                              ? 'Failed'
                              : row.status === 'ATTEMPTING'
                                ? 'Attempting'
                                : 'Unknown'
                        }}
                      </span>
                      @if (row.statusDetail) {
                        <span class="bg-red-500 node-status-detail">{{ row.statusDetail }}</span>
                      }
                    </div>
                  </div>
                </td>
              </ng-container>

              <ng-container matColumnDef="gateway">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Gateway</th>
                <td mat-cell *matCellDef="let row">
                  <div class="flex flex-col">
                    <span>{{ row.gateway }}</span>
                    @if (row.gatewaySerialNumber && row.gatewaySerialNumber !== row.gateway) {
                      <span class="font-medium text-slate-500">{{ row.gatewaySerialNumber }}</span>
                    }
                  </div>
                </td>
              </ng-container>

              <ng-container matColumnDef="firmware">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Firmware</th>
                <td mat-cell *matCellDef="let row" class="font-normal">{{ row.firmware || '-' }}</td>
              </ng-container>

              <ng-container matColumnDef="lastActivity">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Last Activity</th>
                <td mat-cell *matCellDef="let row" class="font-medium text-slate-500">
                  {{ row.lastActivity || '-' }}
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="nodeJoiningColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: nodeJoiningColumns"></tr>
            </table>
          }

          <app-pagination
            [section]="'node-joining'"
            [position]="'bottom'"
            [rows]="nodeJoiningRows()"
            [dataSource]="nodeJoiningDataSource"
          />
        </div>
      </mat-expansion-panel>
    </div>

    <div class="mb-8">
      <mat-expansion-panel class="nms-expansion-panel">
        <mat-expansion-panel-header class="text-lg font-medium text-slate-900" data-cy="session-history-panel-trigger">
          <mat-panel-title class="text-lg font-medium text-slate-900">Session History</mat-panel-title>
        </mat-expansion-panel-header>
        <div class="session-history-panel grid w-full gap-4">
          @if (loading() && !sessionHistoryRows().length) {
            <div class="gateway-table-empty text-slate-700 bg-white" data-cy="session-history-loading">
              {{ 'Loading...' }}
            </div>
          } @else if (!sessionHistoryRows().length) {
            <div class="gateway-table-empty text-slate-700 bg-white" data-cy="session-history-empty">
              {{ 'No Data!' }}
            </div>
          } @else {
            <table
              mat-table
              [dataSource]="sessionHistoryDataSource"
              class="session-history-table"
              data-cy="session-history-table">
              <ng-container matColumnDef="dateTime">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Date &amp; Time</th>
                <td mat-cell *matCellDef="let row">
                  <span class="inline-flex items-center gap-2 text-slate-900">
                    <span class="icon icon-calendar" aria-hidden="true"></span>
                    <span>{{ row.startTime | date:'medium' }}</span>
                  </span>
                </td>
              </ng-container>

              <ng-container matColumnDef="duration">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Duration</th>
                <td mat-cell *matCellDef="let row">{{ formatDuration(row.sessionDurationSeconds) }}</td>
              </ng-container>

              <ng-container matColumnDef="nodesJoined">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Nodes Joined</th>
                <td mat-cell *matCellDef="let row">{{ row.nodesJoined }}</td>
              </ng-container>

              <ng-container matColumnDef="successRate">
                <th mat-header-cell *matHeaderCellDef class="font-semibold text-m text-slate-500">Success Rate</th>
                <td mat-cell *matCellDef="let row">
                  <app-nms-success-rate
                    [successfulCount]="row.nodesJoined"
                    [totalCount]="row.nodesFound"
                  />
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="sessionHistoryColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: sessionHistoryColumns"></tr>
            </table>
          }

          <app-pagination
            [section]="'session-history'"
            [position]="'bottom'"
            [rows]="sessionHistoryRows()"
            [dataSource]="sessionHistoryDataSource" />
        </div>
      </mat-expansion-panel>
    </div>

    @if (bleKeyDeliveryError(); as error) {
      <div class="mb-8">
        <mat-expansion-panel class="nms-expansion-panel" data-cy="ble-key-delivery-err-panel" [disabled]="true">
          <mat-expansion-panel-header class="text-[15px] font-medium text-slate-900" data-cy="ble-card-error">
            <mat-panel-title class="text-[15px] font-medium text-slate-900">BLE Key Delivery -
              <span class="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{{ error }}</span>
            </mat-panel-title>
          </mat-expansion-panel-header>
        </mat-expansion-panel>
      </div>
    }

    @if(showBleKeyDeliveryPanel()) {
      <div class="mb-8">
        <mat-expansion-panel class="nms-expansion-panel"  data-cy="ble-key-delivery-panel">
          <mat-expansion-panel-header class="text-lg font-medium text-slate-900" data-cy="ble-delivery-panel-trigger">
            <mat-panel-title class="text-lg font-medium text-slate-900">BLE Key Delivery</mat-panel-title>
            @if(bleKeyDeliveryResponse().totalNodes > 0) {
              <button
                mat-icon-button
                type="button"
                class="copy-nodes-button rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-500"
                [matTooltip]="'Copy nodes to clipboard'"
                data-cy="ble-delivery-log-copy-nodes"
                (click)="$event.stopPropagation(); copyNodesToClipboard()">
                <span class='icon copy-icon'></span>
              </button>
            }
            @if(retries()>0) {
              <span class="flex items-center gap-2 whitespace-nowrap pr-4">
                <span class="icon icon-retry" aria-hidden="true"></span>
                <span class="text-sm leading-[1.2] font-medium text-amber-600">{{retries()}} retries</span>
              </span>
            }
            @if(allowRetry()) {
              <div class="p-2">
                <button
                  mat-flat-button
                  type="button"
                  class="workflow-retry-btn ble-delivery-log-retry"
                  [attr.data-cy]="'ble-key-step-retry'"
                  [disabled]="bleKeyDeliveryRetrying()"
                  (click)="$event.stopPropagation(); retryBleKeyDelivery()">
                  <span class="retry-btn-content">
                    <span class="icon icon-retry-btn" aria-hidden="true"></span>
                    <span>Retry</span>
                  </span>
                </button>
              </div>
            }
          </mat-expansion-panel-header>
          <div>
            @for(bleKeyDeliveryStep of bleKeyDeliveryResponse().steps; track bleKeyDeliveryStep) {
              <mat-card class="ble-delivery-log m-2"
                        [class.fail-card]="bleKeyDeliveryStep.status === BLEKeyDeliveryStepStatus.Failed || bleKeyDeliveryStep.status === BLEKeyDeliveryStepStatus.Skipped"
                        [class.success-card]="bleKeyDeliveryStep.status === BLEKeyDeliveryStepStatus.Completed"
                        [class.info-card]="bleKeyDeliveryStep.status === BLEKeyDeliveryStepStatus.InProgress || bleKeyDeliveryStep.status === BLEKeyDeliveryStepStatus.Pending"
                        appearance="outlined" data-cy="ble-delivery-log">
                <div class="p-2">
                  <p class="text-slate-400 font-semibold font-mono text-base leading-[1.45]">
                    <span class="ble-delivery-log-level">[INFO]</span>
                    BLE attempted delivery to {{ bleKeyDeliveryStep.totalNodes }} nodes
                  </p>
                  <p class="text-slate-400 font-semibold font-mono text-base leading-[1.45]">
                    <span class="ble-delivery-log-level">[INFO]</span>
                    {{ bleKeyDeliveryStep.completedNodes }}/{{ bleKeyDeliveryStep.totalNodes }} nodes acknowledged new BLE keys
                  </p>
                  @if(bleKeyDeliveryStep.failedNodes) {
                    <p class="text-red-500 font-semibold font-mono text-base leading-[1.45]">
                      <span class="ble-delivery-log-level">[ERROR]</span>
                      {{ bleKeyDeliveryStep.message }}
                    </p>
                  }
                  @if(bleKeyDeliveryStep.pendingNodes) {
                    <p class="text-blue-400 font-semibold font-mono text-base leading-[1.45]">
                      <span class="ble-delivery-log-level">[RUNNING]</span>
                      Awaiting acknowledgement from {{ bleKeyDeliveryStep.pendingNodes }} nodes…
                    </p>
                  }
                </div>
              </mat-card>
            }
          </div>
        </mat-expansion-panel>
      </div>
    } @else if(bleKeyDeliveryBlockedMessage(); as blockedMessage) {
      <div class="mb-8">
        <mat-expansion-panel class="nms-expansion-panel" data-cy="ble-key-delivery-panel" [disabled]="true">
          <mat-expansion-panel-header class="text-[15px] font-medium text-slate-900" data-cy="ble-card-panel-trigger">
            <mat-panel-title class="text-[15px] font-medium text-slate-900">BLE Key Delivery -
              <span class="text-slate-400 italic">{{ blockedMessage }}</span>
            </mat-panel-title>
          </mat-expansion-panel-header>
        </mat-expansion-panel>
      </div>
    }
  </div>
</main>
